import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Clock, LogIn, LogOut, DollarSign, Timer } from "lucide-react";

const fmtSince = (iso) => {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

export default function Timesheets() {
  const { t, lang } = useLang();
  const [data, setData] = useState({ employees: [], active: [] });
  const [projects, setProjects] = useState([]);
  const [proj, setProj] = useState("");
  const [emp, setEmp] = useState("");
  const [employees, setEmployees] = useState([]);

  const load = () => api.get("/timesheets").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const logTime = async (action) => {
    if (!proj) return toast.error(lang === "es" ? "Selecciona un proyecto" : "Select a project");
    if (!emp) return toast.error(lang === "es" ? "Selecciona un empleado" : "Select an employee");
    try {
      await api.post("/projects/time-entry", { project_id: proj, employee_name: emp, action });
      toast.success(action === "check_in" ? (lang === "es" ? "Entrada registrada" : "Clocked in") : (lang === "es" ? "Salida registrada" : "Clocked out"));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const totalHours = data.employees.reduce((a, e) => a + (e.total_hours || 0), 0);
  const totalCost = data.employees.reduce((a, e) => a + (e.total_cost || 0), 0);

  return (
    <div data-testid="timesheets-page">
      <h1 className="font-head text-2xl font-extrabold tracking-tight text-secondary">{lang === "es" ? "Horas trabajadas" : "Timesheets"}</h1>
      <p className="mt-1 text-sm text-slate-500">{lang === "es" ? "Registra entradas y salidas de empleados por proyecto y controla las horas." : "Track employee clock-in/out per project and monitor hours."}</p>

      {/* Summary cards */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="ts-total-hours">
          <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{lang === "es" ? "Horas totales" : "Total hours"}</span><Timer className="h-5 w-5 text-primary" /></div>
          <p className="mt-2 font-head text-3xl font-extrabold text-secondary">{totalHours.toFixed(1)}h</p>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="ts-total-cost">
          <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{lang === "es" ? "Costo mano de obra" : "Labor cost"}</span><DollarSign className="h-5 w-5 text-accent" /></div>
          <p className="mt-2 font-head text-3xl font-extrabold text-accent">${totalCost.toFixed(0)}</p>
        </div>
        <div className="rounded-sm border border-slate-200 bg-white p-5" data-testid="ts-active-count">
          <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{lang === "es" ? "En turno ahora" : "Clocked in now"}</span><Clock className="h-5 w-5 text-primary" /></div>
          <p className="mt-2 font-head text-3xl font-extrabold text-secondary">{data.active.length}</p>
        </div>
      </div>

      {/* Clock in/out panel */}
      <div className="mt-6 rounded-sm border border-slate-200 bg-white p-5">
        <h2 className="font-head text-lg font-bold text-secondary">{lang === "es" ? "Registrar entrada / salida" : "Clock in / out"}</h2>
        <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
          <select value={proj} onChange={(e) => setProj(e.target.value)} className="h-10 flex-1 rounded-sm border border-input px-3 text-sm" data-testid="ts-project-select">
            <option value="">{lang === "es" ? "Selecciona proyecto…" : "Select project…"}</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
          </select>
          <select value={emp} onChange={(e) => setEmp(e.target.value)} className="h-10 flex-1 rounded-sm border border-input px-3 text-sm" data-testid="ts-employee-select">
            <option value="">{lang === "es" ? "Selecciona empleado…" : "Select employee…"}</option>
            {employees.map((e) => <option key={e.id} value={e.name}>{e.name}</option>)}
          </select>
          <div className="flex gap-2">
            <Button onClick={() => logTime("check_in")} className="flex-1 rounded-sm bg-accent hover:bg-emerald-600" data-testid="ts-check-in-btn"><LogIn className="mr-1 h-4 w-4" />{lang === "es" ? "Entrada" : "In"}</Button>
            <Button onClick={() => logTime("check_out")} variant="outline" className="flex-1 rounded-sm" data-testid="ts-check-out-btn"><LogOut className="mr-1 h-4 w-4" />{lang === "es" ? "Salida" : "Out"}</Button>
          </div>
        </div>
      </div>

      {/* Active sessions */}
      {data.active.length > 0 && (
        <div className="mt-6" data-testid="ts-active-list">
          <h2 className="font-head text-lg font-bold text-secondary">{lang === "es" ? "En turno ahora" : "Currently clocked in"}</h2>
          <div className="mt-3 flex flex-wrap gap-3">
            {data.active.map((a, i) => (
              <div key={i} className="flex items-center gap-3 rounded-sm border border-accent/30 bg-accent/5 px-4 py-2.5" data-testid={`ts-active-${a.employee_name}`}>
                <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-accent" /></span>
                <div>
                  <p className="text-sm font-semibold text-secondary">{a.employee_name}</p>
                  <p className="text-xs text-slate-500">{a.title} · {fmtSince(a.since)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Per employee table */}
      <h2 className="mt-8 font-head text-lg font-bold text-secondary">{lang === "es" ? "Resumen por empleado" : "By employee"}</h2>
      <div className="mt-3 overflow-x-auto rounded-sm border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="ts-table">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-400">
              <th className="p-3">{lang === "es" ? "Empleado" : "Employee"}</th>
              <th className="p-3">{lang === "es" ? "$/hora" : "Rate"}</th>
              <th className="p-3">{lang === "es" ? "Horas" : "Hours"}</th>
              <th className="p-3">{lang === "es" ? "Costo" : "Cost"}</th>
              <th className="p-3">{lang === "es" ? "Proyectos" : "Projects"}</th>
            </tr>
          </thead>
          <tbody>
            {data.employees.map((e) => (
              <tr key={e.employee_name} className="border-b border-slate-100" data-testid={`ts-row-${e.employee_name}`}>
                <td className="p-3 font-semibold text-secondary">
                  {e.employee_name}
                  {e.open && <span className="ml-2 rounded-sm bg-accent/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-accent">{lang === "es" ? "En turno" : "Active"}</span>}
                </td>
                <td className="p-3 text-slate-500">${e.hourly_rate}</td>
                <td className="p-3 font-bold text-secondary">{e.total_hours}h</td>
                <td className="p-3 font-bold text-accent">${e.total_cost.toFixed(2)}</td>
                <td className="p-3 text-slate-500">
                  {e.projects.length === 0 ? "—" : e.projects.map((p) => `${p.title} (${p.hours}h)`).join(", ")}
                </td>
              </tr>
            ))}
            {data.employees.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-400">{lang === "es" ? "Agrega empleados y registra tiempo para ver el resumen." : "Add employees and log time to see the summary."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
